import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Delivery, GuidanceDefaults } from '../servicesShared';
import { StewardsSelect } from '../StewardsSelect';
import { DirtySetter, SectionHeader } from './editorShared';

/**
 * Team: the people on the vendor's side. Who answers the job's questions and
 * approvals (live, per service, overridable per job), how long the job waits
 * for them, and where they get notified (part of the versioned definition).
 * Customers never see any of this.
 */
export const TeamTab: React.FC<{
    stewardUserIds: string[];
    onStewardsChange: (ids: string[]) => void;
    guidanceDefaults: GuidanceDefaults;
    onGuidanceDefaultsChange: (next: GuidanceDefaults) => void;
    delivery: Delivery;
    onDeliveryChange: DirtySetter<Delivery>;
}> = ({ stewardUserIds, onStewardsChange, guidanceDefaults, onGuidanceDefaultsChange, delivery, onDeliveryChange }) => {
    const toggleChannel = (channel: 'email' | 'slack', on: boolean) =>
        onDeliveryChange((prev) => {
            const current = (prev.stewards || []) as string[];
            const next = on ? Array.from(new Set([...current, channel])) : current.filter((existing) => existing !== channel);
            return { ...prev, stewards: next } as Delivery;
        });

    return (
        <>
            <section className="mb-10">
                <SectionHeader
                    title="Who handles this service"
                    description="The people on your team the job turns to with questions and approvals. Internal only; customers never see this. Saved live, and overridable per job."
                />
                <StewardsSelect value={stewardUserIds} onChange={onStewardsChange} />
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="When your team does not answer"
                    description="A job that asks your team something keeps working on its stated default. These set how long it waits before reminding and before moving on for good."
                />
                <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
                    <div className="space-y-1">
                        <Label htmlFor="guidance-reminder">Remind after (hours)</Label>
                        <Input
                            id="guidance-reminder"
                            type="number"
                            min={1}
                            max={720}
                            placeholder="24"
                            value={guidanceDefaults.reminder_hours ?? ''}
                            onChange={(event) =>
                                onGuidanceDefaultsChange({ ...guidanceDefaults, reminder_hours: event.target.value === '' ? undefined : Number(event.target.value) })
                            }
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="guidance-deadline">Go with the default after (hours)</Label>
                        <Input
                            id="guidance-deadline"
                            type="number"
                            min={1}
                            max={720}
                            placeholder="48"
                            value={guidanceDefaults.deadline_hours ?? ''}
                            onChange={(event) =>
                                onGuidanceDefaultsChange({ ...guidanceDefaults, deadline_hours: event.target.value === '' ? undefined : Number(event.target.value) })
                            }
                        />
                        <p className="text-xs text-muted-foreground">No job stalls forever on an unanswered question.</p>
                    </div>
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="How your team gets notified"
                    description="Where questions, approvals and alerts for your team land, beyond the Needs you queue. Saved with the draft and published with the version."
                />
                <div className="space-y-2 max-w-xl">
                    {(['email', 'slack'] as const).map((channel) => (
                        <label key={channel} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={(delivery.stewards || []).includes(channel)} onCheckedChange={(checked) => toggleChannel(channel, checked === true)} />
                            {channel === 'email' ? 'Email each person on the team' : 'A Slack channel'}
                        </label>
                    ))}
                    {(delivery.stewards || []).includes('slack') && (
                        <Input
                            className="mt-1 max-w-xs"
                            placeholder="Slack channel id (C0123…)"
                            value={delivery.slack_channel_id || ''}
                            onChange={(event) => onDeliveryChange((prev) => ({ ...prev, slack_channel_id: event.target.value.trim() || undefined }))}
                        />
                    )}
                </div>
            </section>
        </>
    );
};

export default TeamTab;
